import type { AnalyticsHubReportDefinition } from '../../../../../components/api/analyticsHubService';
import type { ReportDefinition, ReportParameter, ReportSettings, ReportType, SourceType } from './types';
import { combineTemplateParts, type TemplateParts } from './templateParts';
import { createEmptyReportParameter } from './reportParameters';

const ICON_ALIASES: Record<string, string> = {
  BarChart3: 'barChart',
  PieChart: 'pieChart',
  LineChart: 'trendUp',
  FileText: 'fileText',
  FileBarChart: 'fileText',
  TrendingUp: 'trendUp',
  Users: 'users',
  MessageSquare: 'message',
  Table: 'fileText',
  ClipboardList: 'fileText',
  Activity: 'trendUp',
  Database: 'fileText',
};

function normalizeReportIcon(icon?: string): string {
  if (!icon) return 'barChart';
  if (ICON_ALIASES[icon]) return ICON_ALIASES[icon];
  if (ICON_ALIASES[icon.charAt(0).toUpperCase() + icon.slice(1)]) {
    return ICON_ALIASES[icon.charAt(0).toUpperCase() + icon.slice(1)];
  }
  return icon in ICON_ALIASES ? icon : 'barChart';
}

function mapParametersFromApi(api: AnalyticsHubReportDefinition): ReportParameter[] {
  if (api.parameters?.length) {
    return api.parameters.map((param) =>
      createEmptyReportParameter({
        name: param.name,
        mapping_type: param.mapping_type,
        isrequired: param.isrequired ?? false,
        value: param.value,
      })
    );
  }

  const legacy: ReportParameter[] = [];
  for (const _value of api.roles ?? []) {
    legacy.push(createEmptyReportParameter({ name: 'roles', mapping_type: 'custom' }));
  }
  for (const _value of api.users ?? []) {
    legacy.push(createEmptyReportParameter({ name: 'users', mapping_type: 'custom' }));
  }
  for (const _value of api.denied ?? []) {
    legacy.push(createEmptyReportParameter({ name: 'denied', mapping_type: 'custom' }));
  }
  for (const _value of api.allowed_departments ?? []) {
    legacy.push(createEmptyReportParameter({ name: 'allowed_departments', mapping_type: 'custom' }));
  }
  return legacy;
}

function buildHtmlTemplate(api: AnalyticsHubReportDefinition): string | undefined {
  const finalHtml = api.final_report?.html;
  if (typeof finalHtml === 'string' && finalHtml.trim()) {
    return finalHtml;
  }

  if (api.jsx || api.css || api.js) {
    return combineTemplateParts({
      html: api.jsx ?? '',
      css: api.css ?? '',
      js: api.js ?? '',
    });
  }

  return undefined;
}

function buildReportSettings(
  api: AnalyticsHubReportDefinition,
  reportDbId: string
): ReportSettings {
  const sourceType = (api.source_type === 'datamodel' ? 'datamodel' : 'workflow') as SourceType;
  const reportType = (api.report_type === 'chatbot' ? 'chatbot' : 'standalone') as ReportType;

  return {
    reportId: api.report_id ?? reportDbId,
    isUniversalId: false,
    reportType,
    description: api.description ?? '',
    icon: api.icon ?? 'BarChart3',
    isActive: true,
    ispublished: api.ispublished ?? false,
    sourceType,
    workflowId: sourceType === 'workflow' ? (api.source_id ?? '') : '',
    dataModelId: sourceType === 'datamodel' ? (api.source_id ?? '') : '',
    parameters: mapParametersFromApi(api),
    copilotIds: api.copilot_ids ?? [],
    reportHeader: (api.report_header as Record<string, unknown>) ?? { title: '', subtitle: '' },
  };
}

export function templatePartsFromAnalyticsHubReport(
  api: AnalyticsHubReportDefinition
): TemplateParts {
  return {
    html: api.jsx ?? '',
    css: api.css ?? '',
    js: api.js ?? '',
  };
}

export function resolveAnalyticsHubCollectionId(
  api: AnalyticsHubReportDefinition,
  fallbackCollectionId: string
): string {
  return (
    api.collection_id ??
    api.analytics_collection_id ??
    api.collectionid ??
    fallbackCollectionId
  );
}

/** MongoDB document id from Analytics Hub (`_id` field). */
export function resolveReportDbId(api: AnalyticsHubReportDefinition): string {
  return (api._id ?? api.reportid ?? '').trim();
}

function resolveReportStatus(
  api: AnalyticsHubReportDefinition,
  hasContent: boolean
): ReportDefinition['status'] {
  if (typeof api.ispublished === 'boolean') {
    return api.ispublished ? 'ready' : 'draft';
  }
  return hasContent ? 'ready' : 'draft';
}

/** Normalize get-by-id payload — API may return one definition or a list. */
export function normalizeReportDefinitionsFromResponse(
  data: AnalyticsHubReportDefinition | AnalyticsHubReportDefinition[] | null | undefined
): AnalyticsHubReportDefinition[] {
  if (!data) return [];
  return Array.isArray(data) ? data : [data];
}

/** Pick the definition to load in the designer (exact id match, else prefer draft). */
export function selectReportDefinitionForEdit(
  items: AnalyticsHubReportDefinition[],
  reportId?: string
): AnalyticsHubReportDefinition | undefined {
  if (!items.length) return undefined;

  if (reportId) {
    const exact = items.find(
      (item) =>
        resolveReportDbId(item) === reportId ||
        (item.report_id ?? '').trim() === reportId
    );
    if (exact) return exact;
  }

  const draft = items.find((item) => item.ispublished === false);
  return draft ?? items[0];
}

export function mapAnalyticsHubReportDefinition(
  api: AnalyticsHubReportDefinition,
  collectionId: string
): ReportDefinition {
  const id = resolveReportDbId(api);
  const htmlTemplate = buildHtmlTemplate(api);
  const hasContent = !!htmlTemplate;
  const resolvedCollectionId = resolveAnalyticsHubCollectionId(api, collectionId);

  return {
    id,
    name: api.report_name,
    description: api.description ?? '',
    category: api.report_type ?? 'Custom',
    icon: normalizeReportIcon(api.icon),
    collectionId: resolvedCollectionId,
    lastGenerated: api.updated_time ?? api.created_time,
    status: resolveReportStatus(api, hasContent),
    htmlTemplate,
    settings: buildReportSettings(api, id),
  };
}
